import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, CalendarDays, Check, ChevronLeft, Flame, MinusCircle, PlusCircle, Star, X } from 'lucide-react';
import { functions } from '@/lib/firebase';
import { Icon3D } from '@/components/Icon3D';
import { useGeneralSettings } from '@/lib/useShulSettings';
import { useAuth } from '@/auth/AuthProvider';
import { usePermissions } from '@/auth/usePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DateDisplay } from '@/components/DateDisplay';
import { Money } from '@/components/MoneyInput';
import { NAV, navVisible } from '@/layout/nav';
import { MyTasksCard } from './home/MyTasksCard';
import { gematriyaYear } from '@/lib/hebrewYear';
import { cn } from '@/lib/utils';

interface Dashboard {
  todayKey: string;
  reading?: { dateKey: string; label: string; hebrewDate: string; minyanim: Array<{ id: string; name: string; aliyot: number; sales: number }> };
  money?: {
    incomeByBox?: Record<string, number>; expenseThisMonth?: number; incomeByMonth?: Array<{ month: string; income: number; expense: number }>;
    openDebts?: number; debtors?: number; guestDebts?: number; donationsYtd?: number; hebYear?: number; kevas?: { active: number; monthly: number; failing: number }; monthKey: string;
  };
  waiting?: Partial<Record<'alerts' | 'alertsUrgent' | 'kevaFailed' | 'kevaStopped' | 'membershipGap' | 'nedarimUnmatched' | 'bankUnmatched' | 'myTasks' | 'loansDue' | 'supportsNoReceipt', number>>;
  upcoming?: Array<{ kind: 'yahrzeit' | 'barMitzvah'; personId: string; name: string; what: string; dateKey: string; hebrewDate: string; shabbatKey?: string; shabbatKeys?: string[] }>;
  activity?: Array<{ id: string; summary: string; actorName: string; at: number; personId: string | null }>;
  notes?: Array<{ dateKey: string; text: string }>;
}

const getDashboard = httpsCallable<Record<string, never>, Dashboard>(functions, 'dashboardSummary');
const MONTHS = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];

function greeting(d = new Date()): string {
  const h = d.getHours();
  if (h < 5) return 'לילה טוב';
  if (h < 12) return 'בוקר טוב';
  if (h < 17) return 'צהריים טובים';
  if (h < 21) return 'ערב טוב';
  return 'לילה טוב';
}

export function HomePage() {
  const { profile } = useAuth();
  const perms = usePermissions();
  const { canRead, canWrite } = perms;
  const { fullName } = useGeneralSettings();
  // the last summary shows at once, then the fresh one replaces it (owner 2026-10-06, speed) — per user, this device only
  const cacheKey = `dashboard.v1.${profile?.uid ?? ''}`;
  const [d, setD] = useState<Dashboard | null>(() => { try { return JSON.parse(localStorage.getItem(cacheKey) ?? 'null') as Dashboard | null; } catch { return null; } });
  const [failed, setFailed] = useState(false);
  const pages = NAV.filter((n) => n.path !== '/' && navVisible(n, perms));

  useEffect(() => {
    getDashboard({}).then((r) => { setD(r.data); try { localStorage.setItem(cacheKey, JSON.stringify(r.data)); } catch { /* private mode */ } })
      .catch(() => setFailed(true));
  }, [cacheKey]);

  const w = d?.waiting ?? {};
  const waitingItems = [
    { n: w.alertsUrgent, label: 'התראות דחופות', to: '/alerts', urgent: true },
    { n: w.loansDue, label: 'החזרי הלוואות קרובים', to: '/loans' },
    { n: w.supportsNoReceipt, label: 'תמיכות בלי אישור קבלה', to: '/supports' },
    { n: w.kevaFailed, label: 'הו״ק שנכשלו', to: '/alerts' },
    { n: w.kevaStopped, label: 'הו״ק שהופסקו', to: '/alerts' },
    { n: w.membershipGap, label: 'פערים בדמי חבר', to: '/alerts' },
    { n: w.bankUnmatched, label: 'שורות בנק לסיווג', to: '/bank' },
  ].filter((x) => x.n);

  return (
    <>
      <section className="mb-6 flex items-center gap-4 overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-[var(--shadow-card)] sm:p-6">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gold-ink">{fullName}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">{greeting()}, {profile?.displayName ?? ''}</h1>
          {d?.reading ? <p className="mt-2 text-[15px] text-ink-2">הקרוב: <b>{d.reading.label}</b> · {d.reading.hebrewDate}</p> : null}
          {canWrite('boxes') ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to="/quick-expense" className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-4 font-semibold text-primary-ink shadow-sm hover:bg-primary/90">
                <MinusCircle className="size-5" /> הוצאה מהירה
              </Link>
              <Link to="/quick-income" className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-primary/30 bg-surface px-4 font-semibold text-primary shadow-sm hover:bg-surface-2">
                <PlusCircle className="size-5" /> הכנסה מהירה
              </Link>
            </div>
          ) : null}
        </div>
        <Icon3D name="hero-building" size={176} eager className="hidden w-44 sm:block" />
      </section>

      {!d && !failed ? (
        <div className="grid gap-4 lg:grid-cols-3"><MyTasksCard /><Skeleton className="h-40" /><Skeleton className="h-40" /></div>
      ) : failed && !d ? (
        <p className="text-ink-2">לא הצלחנו לטעון את הנתונים. אפשר לרענן את הדף.</p>
      ) : d ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <MyTasksCard />
          {waitingItems.length ? (
            <Card className="lg:col-span-3 border-warning/40">
              <CardContent className="flex flex-wrap gap-2 pt-5">
                <span className="flex items-center gap-2 font-semibold"><AlertTriangle className="size-5 text-warning" /> מחכה לטיפול:</span>
                {waitingItems.map((x) => (
                  <Link key={x.label} to={x.to} className={cn('inline-flex min-h-10 items-center gap-2 rounded-full border px-3 text-sm hover:bg-surface-2', x.urgent ? 'border-danger/50 text-danger' : 'border-border')}>
                    <b><bdi>{x.n}</bdi></b> {x.label} <ChevronLeft className="size-4" />
                  </Link>
                ))}
              </CardContent>
            </Card>
          ) : null}

          {d.reading ? (
            <Card>
              <CardHeader>
                <CardTitle>{d.reading.label}</CardTitle>
                <CardDescription><DateDisplay value={d.reading.dateKey} inline showParasha={false} /></CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                {d.reading.minyanim.map((m) => (
                  <Link key={m.id} to={`/services/${d.reading!.dateKey}/${m.id}`} className="flex min-h-11 items-center justify-between gap-2 rounded-xl border border-border px-3 hover:bg-surface-2">
                    <span className="font-medium">{m.name}</span>
                    <span className="flex gap-2 text-sm">
                      <Mark ok={m.aliyot > 0} label="עליות" /><Mark ok={m.sales > 0} label="מכירות" />
                    </span>
                  </Link>
                ))}
                {!d.reading.minyanim.length ? <p className="text-sm text-muted">אין מניינים פעילים.</p> : null}
              </CardContent>
            </Card>
          ) : null}

          {d.money ? <MoneyCard m={d.money} /> : null}

          {d.upcoming ? (
            <Card>
              <CardHeader><CardTitle>בשבועיים הקרובים</CardTitle><CardDescription>יארצייטים ובר מצוות</CardDescription></CardHeader>
              <CardContent>
                {!d.upcoming.length ? <p className="text-sm text-muted">אין אירועים.</p> : (
                  <ul className="flex flex-col gap-2 text-sm">
                    {d.upcoming.map((e, i) => (
                      <li key={i} className="flex items-start gap-2">
                        {e.kind === 'yahrzeit' ? <Flame className="mt-0.5 size-4 shrink-0 text-gold-ink" /> : <Star className="mt-0.5 size-4 shrink-0 text-primary" />}
                        <span className="min-w-0 flex-1">
                          <Link to={`/people/${e.personId}`} className="font-medium hover:underline">{e.name}</Link>
                          <span className="text-ink-2"> · {e.kind === 'yahrzeit' ? `יארצייט ${e.what}` : `בר מצווה — ${e.what}`}</span>
                          <span className="block text-xs text-muted">{e.hebrewDate}{e.shabbatKey ? ` · מפטיר בשבת ${(e.shabbatKeys ?? [e.shabbatKey]).map((k) => k.split('-').reverse().join('/')).join(' ובשבת ')}` : ''}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ) : null}

          {d.notes?.length ? (
            <Card>
              <CardHeader><CardTitle>📝 הערות ביומן — שבועיים קרובים</CardTitle></CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-2 text-sm">
                  {d.notes.map((n, i) => (
                    <li key={i}><Link to="/calendar" className="hover:underline"><bdi className="text-xs text-muted">{n.dateKey.split('-').reverse().join('/')}</bdi> · {n.text}</Link></li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}

          {d.activity?.length ? (
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle>פעולות אחרונות</CardTitle></CardHeader>
              <CardContent>
                <ul className="divide-y divide-border text-sm">
                  {d.activity.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 py-2">
                      <span className="w-28 shrink-0 text-xs text-muted">{new Date(a.at).toLocaleString('he-IL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="min-w-0 flex-1 truncate">{a.summary}</span>
                      <span className="shrink-0 text-xs text-ink-2">{a.actorName}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/audit" className="mt-2 inline-block text-sm text-primary-light hover:underline">ליומן המלא</Link>
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}

      <h2 className="mt-8 mb-3 text-lg font-semibold">העמודים שלך</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {pages.map((n) => (
          <Link key={n.path} to={n.path} className="flex min-h-20 items-center gap-3 rounded-2xl border border-border bg-surface p-4 shadow-[var(--shadow-card)] transition-colors hover:border-primary/30">
            <Icon3D name={n.icon3d} size={44} />
            <span className="font-medium">{n.label}</span>
          </Link>
        ))}
      </div>
      {pages.length === 0 ? (
        <p className="mt-4 flex items-center gap-2 text-ink-2"><CalendarDays className="size-4" /> אין עמודים נוספים בהרשאות שלך.</p>
      ) : null}
    </>
  );
}

function Mark({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5', ok ? 'bg-success-soft text-success-ink' : 'bg-surface-2 text-muted')}>
      {ok ? <Check className="size-3.5" /> : <X className="size-3.5" />}{label}
    </span>
  );
}

function MoneyCard({ m }: { m: NonNullable<Dashboard['money']> }) {
  const max = Math.max(1, ...(m.incomeByMonth ?? []).map((x) => Math.max(x.income, x.expense)));
  const [, mon] = m.monthKey.split('-').map(Number);
  return (
    <Card>
      <CardHeader><CardTitle>כספים</CardTitle><CardDescription>{MONTHS[mon - 1]} — החודש</CardDescription></CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {m.incomeByBox ? (
          <dl className="grid grid-cols-[1fr_auto] gap-y-1">
            <dt className="text-muted">הכנסות — נדרים</dt><dd><Money agorot={m.incomeByBox.nedarim ?? 0} className="font-semibold" /></dd>
            <dt className="text-muted">הכנסות — בנק</dt><dd><Money agorot={m.incomeByBox.bank ?? 0} className="font-semibold" /></dd>
            <dt className="text-muted">הכנסות — מזומן</dt><dd><Money agorot={m.incomeByBox.cash ?? 0} className="font-semibold" /></dd>
            <dt className="text-muted">הוצאות</dt><dd><Money agorot={m.expenseThisMonth ?? 0} className="font-semibold text-danger" /></dd>
          </dl>
        ) : null}
        {m.openDebts !== undefined ? (
          <dl className="grid grid-cols-[1fr_auto] gap-y-1 border-t border-border pt-2">
            <dt className="text-muted">חובות פתוחים (<bdi>{m.debtors}</bdi> אנשים)</dt><dd><Link to="/ledger" className="hover:underline"><Money agorot={m.openDebts} className="font-semibold text-danger" /></Link></dd>
            {m.guestDebts ? <><dt className="text-muted">חובות אורחים</dt><dd><Link to="/ledger" className="hover:underline"><Money agorot={m.guestDebts} className="font-semibold text-warning-ink" /></Link></dd></> : null}
            <dt className="text-muted">תרומות {m.hebYear ? gematriyaYear(m.hebYear) : 'השנה'}</dt><dd><Money agorot={m.donationsYtd ?? 0} className="font-semibold text-success-ink" /></dd>
          </dl>
        ) : null}
        {m.kevas ? (
          <div className="flex items-center justify-between border-t border-border pt-2">
            <span className="text-muted"><bdi>{m.kevas.active}</bdi> הו״ק פעילות{m.kevas.failing ? <Badge tone="danger" className="ms-2">{m.kevas.failing} בסירוב</Badge> : null}</span>
            <span><Money agorot={m.kevas.monthly} className="font-semibold" /> לחודש</span>
          </div>
        ) : null}
        {m.incomeByMonth?.length ? (
          <div className="border-t border-border pt-3" aria-label="הכנסות והוצאות בחצי השנה האחרונה">
            <div className="flex h-24 items-end gap-2">
              {m.incomeByMonth.map((x) => (
                <div key={x.month} className="flex flex-1 flex-col items-center gap-1" title={`${x.month}: הכנסות ${(x.income / 100).toLocaleString('he-IL')} ₪ · הוצאות ${(x.expense / 100).toLocaleString('he-IL')} ₪`}>
                  <div className="flex h-20 w-full items-end justify-center gap-0.5">
                    <div className="w-2.5 rounded-t bg-primary" style={{ height: `${(x.income / max) * 100}%` }} />
                    <div className="w-2.5 rounded-t bg-gold" style={{ height: `${(x.expense / max) * 100}%` }} />
                  </div>
                  <span className="text-[11px] text-muted">{MONTHS[Number(x.month.slice(5)) - 1]}</span>
                </div>
              ))}
            </div>
            <div className="mt-1 flex gap-3 text-xs text-muted"><span className="flex items-center gap-1"><span className="size-2 rounded-sm bg-primary" /> הכנסות</span><span className="flex items-center gap-1"><span className="size-2 rounded-sm bg-gold" /> הוצאות</span></div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
